import { Controller, Control, FieldValues } from "react-hook-form";
import { TextField, Autocomplete, Box } from '@mui/material';
import { useMemo } from 'react';

export type FieldType = "string" | "number" | "date" | "email" | "textarea" | "autocomplete" | "password";

export interface FieldDescriptor<T> {
  name: keyof T;
  label: string;
  type: FieldType;
  multiline?: boolean;
  rows?: number;
  options?: any[] | (() => any[]); // Array or function returning array
  optionLabelKey?: string;
  optionValueKey?: string;
  optionDisableKey?: (option: any) => boolean;
  boxSize?: string;  //in %
  disabled?: boolean;
  onChange?: (event: any, value: any) => void;
  required?: boolean;
  defaultValue?: any;
  inputProps?: any;
}

interface FormFieldProps<T extends FieldValues> {
  control: Control<T>;
  field: FieldDescriptor<T>;
}

export function FormField<T extends FieldValues>({ control, field }: FormFieldProps<T>) {
  const {
    name,
    label,
    type,
    multiline,
    rows,
    options,
    optionLabelKey,
    optionValueKey,
    boxSize,
    disabled,
  } = field;

  // Helper to format date values to yyyy-MM-dd

    const formatDate = (value: Date | string | null | undefined) => {
      if (!value) return "";

      const d = value instanceof Date ? value : new Date(value);

      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    };



  // Resolve options array (function or array)
  // const resolvedOptions = typeof options === "function" ? options() : options ?? [];
  const resolvedOptions = useMemo(() => {
    return typeof options === "function" ? options() : options ?? [];
  }, [options]);


  return (
    <Controller
      name={name as any}
      control={control}
      render={({ field: controllerField, fieldState }) => {
        const commonProps = {
          ...controllerField,
          label,
          fullWidth: true,
          margin: "dense" as const,
          error: !!fieldState.error,
          helperText: fieldState.error?.message,
          multiline,
          rows,
          InputLabelProps: type === "date" ? { shrink: true } : undefined,
          required: field.required ?? false,
          disabled,
          inputProps: field.inputProps,
        };

        if (type === "date") {
          return (
            <Box sx={{ width: boxSize ?? "100%" }}>
            <TextField
              {...commonProps}
              type="date"
              value={formatDate(controllerField.value)}
              onChange={(e) =>
                controllerField.onChange(e.target.value ? new Date(e.target.value) : null)
              }
            />
           </Box>
          );
        }

        if (type === "password") return <Box sx={{ width: boxSize ?? "100%" }}><TextField {...commonProps} type="password" /></Box>;


        if (type === "number") return <Box sx={{ width: boxSize ?? "100%" }}><TextField {...commonProps} type="number" /></Box>;
        if (type === "email") return <Box sx={{ width: boxSize ?? "100%" }}><TextField {...commonProps} type="email" /></Box>;

        if (type === "autocomplete") {
          return (
             <Box sx={{ width: boxSize ?? "100%" }}>
                          <Autocomplete
                disablePortal
                disabled={disabled}
                options={resolvedOptions}
                getOptionLabel={(option) =>
                  optionLabelKey ? option[optionLabelKey] ?? "" : ""
                }

                getOptionDisabled={(option) =>  field.optionDisableKey ? field.optionDisableKey(option) : false}

                value={
                  resolvedOptions.find(
                    (opt) => optionValueKey && opt[optionValueKey] === controllerField.value
                  ) || null
                }
                isOptionEqualToValue={(option, value) =>
                  optionValueKey ? option[optionValueKey] === value[optionValueKey] : option === value
                }
                onChange={(event, newValue) => {
                  // keep only the ID in react-hook-form
                  controllerField.onChange(
                    newValue && optionValueKey ? newValue[optionValueKey] : null
                  );

                  // forward to your descriptor handler
                field.onChange?.(
                    event,
                    newValue && optionValueKey ? newValue[optionValueKey] : null
                  );
                }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={label}
                          margin="dense"
                          fullWidth
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                              </Box>
                              );
        }

        // Default to string
        return  <Box sx={{ width: boxSize ?? "100%" }}>  <TextField {...commonProps} /> </Box>;
      }}
    />
  );
}
